import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Typography, Grid, Divider, CircularProgress, Button } from '@mui/material';
import GoBack from 'js/containers/GoBack.jsx';
import { downloadImage } from 'js/library/services/StorageManager.js';
import placeholderItem from 'styles/assets/placeholder/placeholder.gif';
import {
  getUrlVariables,
  trackEventMatomo,
  trackEventMatomoVisit,
} from 'js/library/utils/helpers';
import { useCurrentUserInfo } from 'js/library/services/currentUserInfo';
import { getUserStatusOrgs } from 'js/library/utils/API/getUserStatusOrgs';
import { insertUserOrgs } from 'js/library/utils/API/insertUserOrgs';
import { removeUserOrgs } from 'js/library/utils/API/removeUserOrgs';
import { getOrgByID } from 'js/library/utils/API/getOrgByID';
import { getCardsByOrgsNode } from 'js/library/utils/API/getCardsByOrgsNode';

import Cards from 'js/components/Organizations/Cards';

export default function Organizations(props) {
  const location = useLocation();
  const { userInfo, loading: userInfoLoading } = useCurrentUserInfo();
  const { t } = useTranslation('app'); //Usado para tradução
  const uId = userInfo?.uId;
  const isAnonymousUser = !userInfo || !uId || userInfo.triiboId?.includes('@guest');

  const [id, setId] = useState(() => getUrlVariables().id);
  const [orgInfo, setOrgInfo] = useState(null);
  const [userStatus, setUserStatus] = useState(null);
  const [downloadedCoverImage, setDownloadedCoverImage] = useState(placeholderItem);
  const [cards, setCards] = useState(null);

  useEffect(() => {
    document.title = 'Organizacao';
    trackEventMatomoVisit('Organizacao');

    let active = true;

    getOrgByID(id)
      .then((resultOrg) => {
        if (active && resultOrg.success) {
          setOrgInfo(resultOrg.orgInfo);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [id]);

  useEffect(() => {
    if (userInfoLoading) return;

    let active = true;

    if (isAnonymousUser) {
      setUserStatus('anonymous');
    } else {
      setUserStatus(null);

      getUserStatusOrgs(uId, id)
        .then((resultOrg) => {
          if (active && resultOrg.success) {
            setUserStatus(resultOrg.status);
          }
        })
        .catch(() => {});
    }

    getCardsByOrgsNode(uId, id)
      .then((result) => {
        if (active && result) {
          setCards(result.cards);
        }
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [id, isAnonymousUser, uId, userInfoLoading]);

  useEffect(() => {
    const urlId = getUrlVariables().id;
    if (urlId && urlId !== id) {
      setId(urlId);
      setOrgInfo(null);
      setUserStatus(null);
      setCards(null);
      setDownloadedCoverImage(placeholderItem);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const downloadImageOnLoad = (data) => {
    if (data !== undefined && data !== null) {
      downloadImage(data.split('/')[0], data.split('/')[1])
        .then((downloaded) => {
          setDownloadedCoverImage(downloaded ?? placeholderItem);
        })
        .catch(() => {
          setDownloadedCoverImage(placeholderItem);
        });
    }
  };

  const handleRequest = async () => {
    if (isAnonymousUser) {
      props.setOpenState(true);
    } else {
      setUserStatus(null);

      await insertUserOrgs(uId, orgInfo.orgID);
      props.reloadOrgs(true);

      getUserStatusOrgs(uId, orgInfo.orgID).then((resultOrg) => {
        if (resultOrg.success) {
          setUserStatus(resultOrg.status);
        }
      });
    }
  };

  const handleRemoveRequest = async () => {
    if (isAnonymousUser) {
      props.setOpenState(true);
    } else {
      setUserStatus(null);

      await removeUserOrgs(uId, orgInfo.orgID);
      props.reloadOrgs(true);

      getUserStatusOrgs(uId, orgInfo.orgID).then((resultOrg) => {
        if (resultOrg.success) {
          setUserStatus(resultOrg.status);
        }
      });
    }
  };

  const handleLoginRequest = () => {
    props.setOpenState(true);
    trackEventMatomo('Organizacao', 'tocar', 'botao', 'Login para participar da triibo');
  };

  return (
    <div>
      {orgInfo === null ? (
        <div style={{ textAlign: 'center' }} className="loading-content">
          <CircularProgress />
        </div>
      ) :(
        <div>
          <GoBack location="Organizacao" />

          <img
            src={downloadedCoverImage}
            onLoad={() => downloadImageOnLoad(orgInfo.coverImage)}
            alt="coverImage"
            width="100%"
          />

          <Typography style={{ fontWeight: 'bolder', color: 'black' }} variant="h6" gutterBottom>
            {orgInfo.name}
          </Typography>

          <Typography style={{ whiteSpace: 'pre-line' }} color="secondary" variant="subtitle1">
            {orgInfo.description}
          </Typography>

          <Divider style={{ margin: '20px 0px' }} />

          {userStatus === null ? (
            <div style={{ textAlign: 'center' }}>
              <CircularProgress />
            </div>
          ) : (
            <div>
              <Grid container spacing={3} style={{ width: '100%', marginBottom: '20px' }}>
                <Grid size={{ md: 4, xs: 12 }}></Grid>

                <Grid size={{ md: 4, xs: 12 }}>
                  {userStatus === 'anonymous' ? (
                    <>
                      <Typography
                        align="center"
                        color="secondary"
                        variant="body2"
                        style={{ marginBottom: '12px' }}
                      >
                        {t('organizations.login_required_to_join_triibo')}
                      </Typography>
                      <Button
                        fullWidth
                        onClick={handleLoginRequest}
                        variant="contained"
                        color="primary"
                        style={{ textTransform: 'none' }}
                      >
                        {t('organizations.go_to_login')}
                      </Button>
                    </>
                  ) : userStatus === 'non-participant' ? (
                    <Button
                      fullWidth
                      onClick={() => {
                        handleRequest();
                        trackEventMatomo('Organizacao', 'tocar', 'botao', 'Juntar-se a triibo');
                      }}
                      variant="contained"
                      color="primary"
                      style={{ textTransform: 'none' }}
                    >
                      {t('organizations.join_triibo')}
                    </Button>
                  ) : orgInfo.lockdown ? null : (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => {
                        handleRemoveRequest();
                        trackEventMatomo('Organizacao', 'tocar', 'botao', 'Sair da triibo');
                      }}
                      color="primary"
                      style={{ textTransform: 'none' }}
                    >
                      {t('organizations.leave_triibo')}
                    </Button>
                  )}
                </Grid>

                <Grid size={{ md: 4, xs: 12 }}></Grid>
              </Grid>

              <div>
                {cards === null ? (
                  <div style={{ textAlign: 'center' }} className="loading-content">
                    <CircularProgress />
                  </div>
                ) : (
                  cards.map((card) => (
                    <Cards cardInfo={card} key={card.cardId} origin="Organizacao" uId={uId} />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
